/*!
 * Backpack OS Runtime 1.0.5
 * https://cdn.backpack.works/webflow/runtime/1.0.5/backpack-runtime.js
 *
 * The only JavaScript Backpack puts on a customer's site. One reviewed
 * file at one immutable versioned URL, applied only to pages that hold a
 * component asking for one of the behaviours below.
 *
 * THIS FILE IS THE SOURCE, NOT THE SERVED ARTIFACT.
 *
 * It is minified by scripts/build-webflow-runtime.mjs into
 * public/webflow/runtime/1.0.5/backpack-runtime.js, and that built file is
 * what customers load and what the registered SRI hash covers. The build
 * is deterministic and `--check` fails if the two ever drift.
 *
 * Served commented for a while, on the argument that a reviewer could read
 * the exact bytes at the URL. Comments alone were 46% of the transferred
 * payload, and that cost lands on customers' visitors rather than on us.
 * Integrity comes from the hash; readability comes from this file and from
 * the source map shipped beside the build.
 *
 * ARCHITECTURE
 *
 *   easing        pure functions, no DOM
 *   helpers       config reading, guards, animation, viewport
 *   behaviours    one self-contained module each, registered by name
 *   scanner       finds [data-backpack] and dispatches, once per element
 *
 * A behaviour never reaches into another behaviour. The registry is the
 * only thing that knows the whole list, and the scanner initialises only
 * what is actually present on the page.
 *
 * CONTRACT
 *
 *   data-backpack="reveal"              turns a behaviour on
 *   data-backpack-duration="600"        configures it
 *   data-backpack-part="slide"          marks a structural child
 *   data-backpack-for / -id             pairs children across a group
 *
 * Every value is a scalar: a number, a bounded enum, or short text. There
 * is no expression syntax, no selector input and no callback hook, because
 * a configuration format that can express logic is a code path wearing a
 * costume, and this file has to be reviewable as the whole of what runs.
 *
 * CONSTRAINTS, enforced against these bytes by check-webflow-runtime.mjs
 * rather than trusted because they are written here:
 *
 *   1. No network. No fetch, XMLHttpRequest, WebSocket, sendBeacon, or
 *      dynamic import.
 *   2. No storage. No cookie, localStorage, sessionStorage, IndexedDB.
 *   3. No dynamic code. No eval, no Function constructor, no string
 *      timers, no innerHTML or insertAdjacentHTML from data.
 *   4. No form or credential access. Inputs, textareas, selects, forms,
 *      labels and contenteditable regions are refused as targets, and so
 *      is anything inside a form.
 *   5. No native overrides. Nothing on Object, Array, Element, Event or
 *      any other built-in prototype is written.
 *   6. One global, window.__backpackRuntime, carrying a version string.
 *   7. No behaviour without an opt-in attribute. An element with no
 *      data-backpack attribute is never touched.
 *
 * Reduced motion is honoured by every behaviour, and honoured by finishing
 * rather than by hiding: a count-up shows its final number, a reveal
 * starts visible, a marquee stands still. Nothing is ever left invisible
 * or unreadable because someone asked for less movement.
 */
(function () {
  "use strict";

  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__backpackRuntime) return;

  var VERSION = "1.0.5";

  var ATTR = "data-backpack";
  var OPT = "data-backpack-";
  var PART = "data-backpack-part";
  var STATE = "data-backpack-state";
  var READY = "data-backpack-ready";
  var CLONE = "data-backpack-clone";

  /* ─────────────────────────────────────────────────────────────────
   * Easing
   *
   * EASING PRESETS. No third-party code is bundled here and none is
   * fetched. These are ordinary mathematical functions written for this
   * file. The names deliberately match the ones most motion tooling uses,
   * because that is the vocabulary people already have and the generator
   * prompt is easier to write against a familiar one, but nothing from
   * any animation library ships in this runtime.
   *
   * Pure functions of t; no DOM, no state, trivially testable. `back` and
   * `elastic` overshoot outside 0..1 on purpose, which is the point of
   * them on a position and wrong on an alpha, so opacity is clamped at
   * the one place it is written.
   * ───────────────────────────────────────────────────────────────── */

  function pow(n) {
    return function (t) {
      return Math.pow(t, n);
    };
  }
  function outOf(fn) {
    return function (t) {
      return 1 - fn(1 - t);
    };
  }
  function inOutOf(fn) {
    return function (t) {
      return t < 0.5 ? fn(t * 2) / 2 : 1 - fn((1 - t) * 2) / 2;
    };
  }

  var backIn = function (t) {
    return t * t * (2.70158 * t - 1.70158);
  };
  var elasticIn = function (t) {
    if (t === 0 || t === 1) return t;
    return -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * ((2 * Math.PI) / 3));
  };
  var expoIn = function (t) {
    return t === 0 ? 0 : Math.pow(2, 10 * t - 10);
  };
  var circIn = function (t) {
    return 1 - Math.sqrt(1 - Math.min(1, t) * Math.min(1, t));
  };

  var EASINGS = {
    none: function (t) {
      return t;
    },
    linear: function (t) {
      return t;
    },
  };

  (function buildEasings() {
    var powers = [
      ["power1", pow(2)],
      ["power2", pow(3)],
      ["power3", pow(4)],
      ["power4", pow(5)],
    ];
    var named = powers.concat([
      ["back", backIn],
      ["elastic", elasticIn],
      ["expo", expoIn],
      ["circ", circIn],
    ]);
    for (var i = 0; i < named.length; i++) {
      var key = named[i][0];
      var fn = named[i][1];
      EASINGS[key + ".in"] = fn;
      EASINGS[key + ".out"] = outOf(fn);
      EASINGS[key + ".inout"] = inOutOf(fn);
      // A bare name means .out, which is the convention everywhere and
      // what a person almost always wants for an entrance.
      EASINGS[key] = outOf(fn);
    }
  })();

  /** Every legal easing name, built from the table so the two cannot
   *  drift. Behaviours validate against this with `pick` rather than
   *  reading free text and relying on the fallback below. */
  var EASING_NAMES = (function () {
    var out = [];
    for (var key in EASINGS) {
      if (Object.prototype.hasOwnProperty.call(EASINGS, key)) out.push(key);
    }
    return out;
  })();

  function easing(name) {
    var key = String(name == null ? "" : name).trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(EASINGS, key)) return EASINGS[key];
    return EASINGS["power2.out"];
  }

  /* ─────────────────────────────────────────────────────────────────
   * Guards
   * ───────────────────────────────────────────────────────────────── */

  // Anything that takes typing, holds a credential, or belongs to a form.
  // A behaviour that moved, hid or re-parented one of these could turn a
  // decorative request into a data problem, so they are refused as
  // targets outright rather than handled carefully.
  var FORBIDDEN_SELF =
    "input,textarea,select,option,form,fieldset,label,button[type=submit],[contenteditable]";
  var FORBIDDEN_ANCESTOR = "form,fieldset,[contenteditable]";

  function isOffLimits(el) {
    if (!el || el.nodeType !== 1) return true;
    try {
      if (el.matches(FORBIDDEN_SELF)) return true;
      if (el.closest(FORBIDDEN_ANCESTOR)) return true;
    } catch (err) {
      // A selector engine that throws is a browser we do not understand.
      // Refuse rather than proceed.
      return true;
    }
    return false;
  }

  function reducedMotion() {
    try {
      return (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    } catch (err) {
      return false;
    }
  }

  /* ─────────────────────────────────────────────────────────────────
   * Config
   *
   * Every read is bounded and every read has a fallback, so a missing or
   * nonsensical attribute degrades to the default rather than to NaN.
   * The panel validates these on the way in as well; this is the second
   * of the two checks, because the runtime also meets hand-edited markup
   * once a component is on a customer's canvas.
   * ───────────────────────────────────────────────────────────────── */

  function raw(el, name) {
    if (!el || typeof el.getAttribute !== "function") return null;
    var v = el.getAttribute(OPT + name);
    return v == null ? null : String(v).trim();
  }

  function num(el, name, fallback, min, max) {
    var v = raw(el, name);
    if (v === null || v === "") return fallback;
    var n = Number(v);
    if (!isFinite(n)) return fallback;
    if (typeof min === "number" && n < min) return min;
    if (typeof max === "number" && n > max) return max;
    return n;
  }

  function bool(el, name, fallback) {
    var v = raw(el, name);
    if (v === null || v === "") return fallback;
    v = v.toLowerCase();
    if (v === "true" || v === "1" || v === "yes") return true;
    if (v === "false" || v === "0" || v === "no") return false;
    return fallback;
  }

  function pick(el, name, allowed, fallback) {
    var v = raw(el, name);
    if (v === null) return fallback;
    v = v.toLowerCase();
    for (var i = 0; i < allowed.length; i++) {
      if (allowed[i] === v) return v;
    }
    return fallback;
  }

  /**
   * A pairing identifier: letters, digits, dash, underscore, bounded.
   *
   * The panel validates these before a component reaches the canvas, and
   * this is the second of the two checks, because the runtime also meets
   * markup a person hand-edited in the Designer where that validator
   * never ran. Anything else reads as absent, so a malformed id fails
   * closed: the modal simply cannot be opened by name.
   */
  function ident(el, name) {
    var v = raw(el, name);
    if (v === null || v === "" || v.length > 40) return "";
    return /^[A-Za-z0-9_-]+$/.test(v) ? v : "";
  }

  function text(el, name, fallback, maxLength) {
    var v = raw(el, name);
    if (v === null) return fallback;
    return v.slice(0, maxLength || 40);
  }

  /* ─────────────────────────────────────────────────────────────────
   * Structure
   * ───────────────────────────────────────────────────────────────── */

  /**
   * The descendants of `scope` marked as `name` that belong to `owner`.
   *
   * The ownership check is the whole point. Two carousels one inside the
   * other, or a reveal wrapping a carousel, would otherwise have the
   * outer one stealing the inner one's slides. Every part walks back up
   * to the nearest [data-backpack] ancestor and is kept only if that
   * ancestor is the owning behaviour root.
   *
   * `scope` and `owner` differ when a behaviour searches inside one of
   * its own sub-containers: an accordion looking for the trigger inside
   * a given item searches the item but still owns it from the root. The
   * first version conflated the two, so the nearest [data-backpack]
   * ancestor of a trigger was the accordion root while the comparison
   * was against the item, no trigger ever matched, and every accordion
   * silently did nothing. Found by running it, not by reading it.
   */
  function parts(scope, name, owner) {
    var out = [];
    if (!scope || typeof scope.querySelectorAll !== "function") return out;
    var holder = owner || scope;
    // Selects on the attribute's PRESENCE, which is a constant, then
    // compares the value with ===. Every caller passes a literal today,
    // so building a selector would be safe today; it is not built anyway,
    // because that is exactly the shape of the scroll-progress bug and
    // one future caller passing a variable is all it would take.
    var found;
    try {
      found = scope.querySelectorAll("[" + PART + "]");
    } catch (err) {
      return out;
    }
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      if (el.getAttribute(PART) !== name) continue;
      var nearest = null;
      try {
        nearest = el.parentElement ? el.parentElement.closest("[" + ATTR + "]") : null;
      } catch (err) {
        nearest = null;
      }
      if (nearest === holder) out.push(el);
    }
    return out;
  }

  function part(scope, name, owner) {
    var all = parts(scope, name, owner);
    return all.length ? all[0] : null;
  }

  /**
   * The element carrying `data-backpack-id="name"`, compared literally.
   *
   * Selects on the attribute's PRESENCE, which is a constant, then
   * compares with ===. Nothing is interpolated, so a hostile value can
   * never be syntax. Two elements sharing an id resolve to the first in
   * document order, deterministically.
   */
  function byId(name) {
    if (!name) return null;
    var candidates;
    try {
      candidates = document.querySelectorAll("[" + OPT + "id]");
    } catch (err) {
      return null;
    }
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].getAttribute(OPT + "id") === name) return candidates[i];
    }
    return null;
  }

  /**
   * The children a choreography behaviour animates.
   *
   * Elements marked data-backpack-part="item" if there are any, otherwise
   * every direct child. Marking wins so a section can hold a heading and
   * a grid and animate only the grid cells; without marking the obvious
   * reading is "all of them", which is what most components want and
   * means the generator can omit the marking entirely.
   */
  function ownedItems(root) {
    var marked = parts(root, "item");
    return marked.length ? marked : kids(root);
  }

  function kids(el) {
    var out = [];
    if (!el || !el.children) return out;
    for (var i = 0; i < el.children.length; i++) out.push(el.children[i]);
    return out;
  }

  function setState(el, value) {
    if (!el || typeof el.setAttribute !== "function") return;
    if (value === null) {
      if (typeof el.removeAttribute === "function") el.removeAttribute(STATE);
      return;
    }
    el.setAttribute(STATE, value);
  }

  /* ─────────────────────────────────────────────────────────────────
   * Animation
   *
   * One rAF loop helper. Behaviours never call requestAnimationFrame
   * directly, so there is exactly one place where a frame budget, a
   * reduced-motion short circuit or a cancel has to be right.
   * ───────────────────────────────────────────────────────────────── */

  function now() {
    try {
      if (window.performance && typeof window.performance.now === "function") {
        return window.performance.now();
      }
    } catch (err) {
      /* fall through */
    }
    return new Date().getTime();
  }

  /**
   * Run `onFrame(progressEased, progressLinear)` for `duration` ms.
   *
   * Under reduced motion it calls onFrame(1, 1) once, synchronously, and
   * finishes. That is the rule the whole file follows: less motion means
   * arrive immediately, never means do not arrive.
   */
  function tween(duration, ease, onFrame, onDone) {
    if (duration <= 0 || reducedMotion()) {
      onFrame(1, 1);
      if (onDone) onDone();
      return function () {};
    }
    var start = now();
    var raf = null;
    var cancelled = false;
    var fn = easing(ease);

    function step() {
      if (cancelled) return;
      var elapsed = now() - start;
      var t = elapsed / duration;
      if (t >= 1) {
        onFrame(1, 1);
        if (onDone) onDone();
        return;
      }
      onFrame(fn(t), t);
      raf = window.requestAnimationFrame(step);
    }

    if (typeof window.requestAnimationFrame !== "function") {
      onFrame(1, 1);
      if (onDone) onDone();
      return function () {};
    }
    raf = window.requestAnimationFrame(step);

    return function cancel() {
      cancelled = true;
      if (raf !== null && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(raf);
      }
    };
  }

  /**
   * Shared IntersectionObservers, one per distinct threshold.
   *
   * Every entrance behaviour used to construct its own observer, so a
   * page with fifty reveals held fifty of them. Thresholds come from a
   * bounded numeric option and components overwhelmingly use the default,
   * so in practice a page now holds one or two observers no matter how
   * many elements are watched.
   *
   * Keyed by the threshold rounded to three places: two components asking
   * for 0.2 and 0.2000001 want the same observer, and an unbounded key
   * space would defeat the pooling.
   */
  var enterPools = Object.create(null);

  function poolFor(threshold) {
    var key = threshold.toFixed(3);
    var pool = enterPools[key];
    if (pool) return pool;

    var watched = new Map();
    var io = new window.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          var record = watched.get(entry.target);
          if (!record) continue;
          if (entry.isIntersecting) {
            record.enter();
            if (record.once) {
              watched.delete(entry.target);
              io.unobserve(entry.target);
            }
          } else if (record.leave) {
            record.leave();
          }
        }
      },
      { threshold: Number(key) }
    );
    pool = { io: io, watched: watched };
    enterPools[key] = pool;
    return pool;
  }

  /**
   * Watch `el` and call `enter` when it crosses `threshold`, and `leave`
   * when it goes back out if a leave handler is given.
   *
   * Falls back to calling `enter` immediately where IntersectionObserver
   * does not exist, because the failure mode that matters is content that
   * never appears. Arriving early is a cosmetic loss; never arriving is a
   * broken page.
   */
  function watchViewport(el, threshold, enter, once, leave) {
    if (typeof window.IntersectionObserver !== "function") {
      enter();
      return function () {};
    }
    var bounded = Math.max(0, Math.min(1, threshold));
    var pool = poolFor(bounded);
    pool.watched.set(el, { enter: enter, leave: leave || null, once: once !== false });
    pool.io.observe(el);
    return function () {
      pool.watched.delete(el);
      pool.io.unobserve(el);
    };
  }

  /** The entrance case: fire once when it comes into view. */
  function onEnter(el, threshold, cb, once) {
    return watchViewport(el, threshold, cb, once);
  }

  /**
   * One scroll subscription shared by everything scroll-linked, so ten
   * parallax elements cost one listener and one frame rather than ten.
   *
   * Each subscriber is paired with the element it exists for. A CMS list
   * that swaps its items, or a Designer session adding and removing
   * sections, would otherwise leave the callback list growing forever and
   * every frame doing measurement work for elements nobody can see. The
   * check is `isConnected`, which is a boolean read and costs nothing.
   */
  var scrollSubs = [];
  var scrollQueued = false;

  function runScrollSubs() {
    scrollQueued = false;
    var live = [];
    for (var i = 0; i < scrollSubs.length; i++) {
      var sub = scrollSubs[i];
      if (sub.el && sub.el.isConnected === false) continue;
      live.push(sub);
      try {
        sub.fn();
      } catch (err) {
        /* one bad subscriber must not stop the others */
      }
    }
    if (live.length !== scrollSubs.length) scrollSubs = live;
  }

  function queueScroll() {
    if (scrollQueued) return;
    scrollQueued = true;
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(runScrollSubs);
    } else {
      runScrollSubs();
    }
  }

  var scrollBound = false;

  function onScroll(fn, el) {
    scrollSubs.push({ fn: fn, el: el || null });
    if (!scrollBound) {
      scrollBound = true;
      window.addEventListener("scroll", queueScroll, { passive: true });
      window.addEventListener("resize", queueScroll, { passive: true });
    }
    // Position correctly on the first paint rather than on first scroll.
    queueScroll();
    return function () {
      for (var i = 0; i < scrollSubs.length; i++) {
        if (scrollSubs[i].fn === fn) {
          scrollSubs.splice(i, 1);
          return;
        }
      }
    };
  }

  function viewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  /** The five directions an entrance can come from, as pixel offsets. */
  var OFFSETS = {
    up: [0, 1],
    down: [0, -1],
    left: [1, 0],
    right: [-1, 0],
    none: [0, 0],
  };

  /** Scaled to a distance. Returns a fresh pair so a caller cannot
   *  mutate the shared table. */
  function offsetFor(direction, distance) {
    var base = Object.prototype.hasOwnProperty.call(OFFSETS, direction)
      ? OFFSETS[direction]
      : OFFSETS.up;
    return [base[0] * distance, base[1] * distance];
  }

  function clamp01(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  /** Alpha only. `back` and `elastic` leave 0..1 by design, which reads
   *  well on a position and is invalid on an opacity: a negative one is
   *  ignored by CSS, so the element jumps instead of fading. */
  function setOpacity(el, value) {
    if (el && el.style) el.style.opacity = String(clamp01(value));
  }

  /* ═════════════════════════════════════════════════════════════════
   * BEHAVIOURS
   *
   * Each is a function of one element. Each is responsible for its own
   * idempotency beyond the READY marker the scanner sets, its own
   * reduced-motion behaviour, and its own accessibility. None of them
   * knows another exists.
   * ═════════════════════════════════════════════════════════════════ */

  /* ── reveal ────────────────────────────────────────────────────────
   *
   * Entrance animation on first scroll into view.
   *
   * NOTHING IS HIDDEN BY A STYLESHEET. The start state is written as an
   * inline style at init and removed again when the animation finishes,
   * so the element ends up back on whatever its own CSS says. A
   * stylesheet that pre-hid revealable elements would look better on a
   * slow first paint and would leave the page permanently blank if this
   * file ever failed to load, which is not a trade worth making on
   * somebody else's site.
   */
  function initReveal(el) {
    var direction = pick(el, "direction", ["up", "down", "left", "right", "none"], "up");
    var distance = num(el, "distance", 24, 0, 400);
    var duration = num(el, "duration", 600, 0, 6000);
    var delay = num(el, "delay", 0, 0, 6000);
    var ease = pick(el, "easing", EASING_NAMES, "power2.out");
    var threshold = num(el, "threshold", 0.15, 0, 1);
    var once = bool(el, "once", true);
    var stagger = num(el, "stagger", 0, 0, 2000);
    var fade = bool(el, "fade", true);
    var scaleFrom = num(el, "scale", 1, 0.5, 1.5);
    var offset = num(el, "offset", 0, 0, 200);

    /* WHAT THIS ONE BEHAVIOUR REPLACES.
     *
     * A staggered section entrance, a scale-and-fade entrance, and a card
     * cascade were all specified as separate behaviours. They are this
     * one with different numbers: stagger sequences the children, scale
     * starts them slightly small, offset adds a per-item vertical step on
     * top of the stagger. Three behaviours a reviewer would read as one
     * behaviour written three times, expressed instead as three options
     * on the behaviour it already was.
     *
     * With a stagger or marked items, the children move and the element
     * itself does not. Otherwise the element moves. That is the whole of
     * the sequencing model: a delay multiplier over owned children,
     * deliberately not a timeline language. */
    var marked = parts(el, "item");
    var targets = marked.length ? marked : stagger > 0 ? kids(el) : [el];
    if (!targets.length) targets = [el];

    var off = offsetFor(direction, distance);

    /** translate and scale composed in one declaration, because two
     *  transform assignments would overwrite each other. */
    function transformAt(p, index) {
      var step = offset * index;
      var x = off[0] * (1 - p);
      var y = (off[1] + step) * (1 - p);
      var bits = "";
      if (x || y) bits += "translate3d(" + x + "px," + y + "px,0)";
      if (scaleFrom !== 1) {
        bits += (bits ? " " : "") + "scale(" + (scaleFrom + (1 - scaleFrom) * p) + ")";
      }
      return bits;
    }

    function start(target, index) {
      if (!target.style) return;
      if (fade) setOpacity(target, 0);
      var t = transformAt(0, index);
      if (t) target.style.transform = t;
      target.style.willChange = fade || t ? "opacity, transform" : "";
    }

    function clear(target) {
      if (!target.style) return;
      target.style.opacity = "";
      target.style.transform = "";
      target.style.willChange = "";
    }

    function play(target, index) {
      var wait = delay + stagger * index;
      var run = function () {
        tween(
          duration,
          ease,
          function (p) {
            if (!target.style) return;
            if (fade) setOpacity(target, p);
            var t = transformAt(p, index);
            if (t) target.style.transform = t;
          },
          function () {
            clear(target);
            setState(target, "revealed");
          }
        );
      };
      if (wait > 0 && !reducedMotion()) window.setTimeout(run, wait);
      else run();
    }

    if (reducedMotion()) {
      // Already where it should be. Mark it so CSS can tell, and stop.
      for (var r = 0; r < targets.length; r++) setState(targets[r], "revealed");
      return;
    }

    for (var i = 0; i < targets.length; i++) start(targets[i], i);

    onEnter(
      el,
      threshold,
      function () {
        for (var j = 0; j < targets.length; j++) play(targets[j], j);
      },
      once
    );
  }

  /* ── countup ───────────────────────────────────────────────────── */

  function formatNumber(value, decimals, separator) {
    var fixed = Math.abs(value).toFixed(decimals);
    var pieces = fixed.split(".");
    var whole = pieces[0];
    if (separator) {
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    var out = pieces.length > 1 ? whole + "." + pieces[1] : whole;
    return (value < 0 ? "-" : "") + out;
  }

  function initCountUp(el) {
    // The target may be stated, or it may be the number already sitting
    // in the element. Reading it from the text means a component that
    // renders "12,000" animates correctly with no extra attribute, and
    // that a component whose script never runs still shows the right
    // figure rather than a zero.
    var stated = raw(el, "to");
    var fromText = String(el.textContent || "").replace(/[^0-9.eE+-]/g, "");
    var to = Number(stated !== null && stated !== "" ? stated : fromText);
    if (!isFinite(to)) return;

    var from = num(el, "from", 0, -1e12, 1e12);
    var duration = num(el, "duration", 1600, 0, 20000);
    var decimals = num(el, "decimals", 0, 0, 4);
    var separator = bool(el, "separator", true);
    var prefix = text(el, "prefix", "", 12);
    var suffix = text(el, "suffix", "", 12);
    var ease = pick(el, "easing", EASING_NAMES, "power2.out");
    var threshold = num(el, "threshold", 0.2, 0, 1);
    var once = bool(el, "once", true);

    function render(value) {
      el.textContent = prefix + formatNumber(value, decimals, separator) + suffix;
    }

    // Announce the final value rather than every intermediate one. A
    // screen reader counting from zero to twelve thousand out loud is
    // the accessibility failure this attribute prevents.
    if (typeof el.setAttribute === "function") {
      el.setAttribute("aria-live", "off");
      if (!el.getAttribute("aria-label")) {
        el.setAttribute(
          "aria-label",
          prefix + formatNumber(to, decimals, separator) + suffix
        );
      }
    }

    if (reducedMotion()) {
      render(to);
      setState(el, "done");
      return;
    }

    render(from);
    onEnter(
      el,
      threshold,
      function () {
        tween(
          duration,
          ease,
          function (p) {
            render(from + (to - from) * p);
          },
          function () {
            render(to);
            setState(el, "done");
          }
        );
      },
      once
    );
  }

  /* ── marquee ───────────────────────────────────────────────────── */

  function initMarquee(el) {
    var speed = num(el, "speed", 40, 1, 400);
    var direction = pick(el, "direction", ["left", "right"], "left");
    var pauseHover = bool(el, "pause-hover", true);
    var gap = num(el, "gap", -1, 0, 400);

    var track = part(el, "track") || el;
    var originals = kids(track);
    if (!originals.length) return;

    if (reducedMotion()) {
      // Standing still and fully readable, with no duplicates cluttering
      // the row. A marquee that hides itself under reduced motion is a
      // logo strip the person can no longer see.
      setState(el, "static");
      return;
    }

    if (track.style) {
      track.style.display = "flex";
      track.style.flexWrap = "nowrap";
      track.style.willChange = "transform";
      if (gap >= 0) track.style.gap = gap + "px";
    }
    if (el !== track && el.style) el.style.overflow = "hidden";
    else if (el.style) el.style.overflow = "hidden";

    // One duplicate set is enough for a seamless loop as long as the
    // original set is at least as wide as the container, which is the
    // case for any strip worth scrolling. Cloned nodes are marked and
    // hidden from assistive technology so the content is not read twice.
    for (var i = 0; i < originals.length; i++) {
      var copy = originals[i].cloneNode(true);
      if (typeof copy.setAttribute === "function") {
        copy.setAttribute(CLONE, "true");
        copy.setAttribute("aria-hidden", "true");
      }
      track.appendChild(copy);
    }

    var offset = 0;
    var paused = false;
    var visible = false;
    var running = false;
    var last = now();
    var sign = direction === "right" ? 1 : -1;
    var cachedSpan = 0;

    /**
     * How far one full set of children reaches, measured once and again
     * only on resize.
     *
     * This used to be recomputed every frame, which meant a layout read
     * per child at sixty frames a second for as long as the page was
     * open: a five-logo strip cost three hundred forced reads a second
     * and never stopped. The width of a logo does not change between
     * frames, and when it does, resize is where it happens.
     */
    function measureSpan() {
      var total = 0;
      for (var k = 0; k < originals.length; k++) total += originals[k].offsetWidth || 0;
      // Include the gaps between the original set so the wrap point lands
      // on a whole cycle rather than drifting by the gap width.
      if (gap >= 0) total += gap * originals.length;
      cachedSpan = total;
    }

    function frame() {
      // THE LOOP STOPS. It used to schedule itself unconditionally, so a
      // marquee that was paused, scrolled far offscreen, or removed from
      // the document entirely still woke the compositor sixty times a
      // second forever. Three conditions end it, and each one restarts it
      // when the reason goes away.
      if (!el.isConnected) {
        running = false;
        return;
      }
      if (paused || !visible) {
        running = false;
        return;
      }
      var t = now();
      var dt = (t - last) / 1000;
      last = t;
      if (cachedSpan > 0) {
        offset += sign * speed * dt;
        if (offset <= -cachedSpan) offset += cachedSpan;
        if (offset >= cachedSpan) offset -= cachedSpan;
        track.style.transform = "translate3d(" + offset + "px,0,0)";
      }
      window.requestAnimationFrame(frame);
    }

    function run() {
      if (running || paused || !visible || !el.isConnected) return;
      if (typeof window.requestAnimationFrame !== "function") return;
      running = true;
      last = now();
      window.requestAnimationFrame(frame);
    }

    function halt() {
      paused = true;
    }

    function resume() {
      paused = false;
      run();
    }

    if (pauseHover) {
      el.addEventListener("mouseenter", halt);
      el.addEventListener("mouseleave", resume);
      el.addEventListener("focusin", halt);
      el.addEventListener("focusout", resume);
    }

    measureSpan();
    // Shares the one throttled resize subscription rather than adding a
    // listener, and drops itself when the element leaves the document.
    onScroll(measureSpan, el);

    // Nothing animates while it is out of view. A page with six logo
    // strips down its length now runs one loop, not six.
    watchViewport(
      el,
      0,
      function () {
        visible = true;
        run();
      },
      false,
      function () {
        visible = false;
      }
    );

    setState(el, "running");
  }

  /* ── carousel ──────────────────────────────────────────────────── */

  function initCarousel(el) {
    var slides = parts(el, "slide");
    if (slides.length < 2) return;

    var interval = num(el, "interval", 0, 0, 60000);
    var loop = bool(el, "loop", true);
    var duration = num(el, "duration", 450, 0, 4000);
    var ease = pick(el, "easing", EASING_NAMES, "power2.out");
    var pauseHover = bool(el, "pause-hover", true);
    var label = text(el, "label", "Carousel", 60);

    var track = slides[0].parentElement;
    if (!track) return;
    var viewport = track.parentElement || el;

    var prev = part(el, "prev");
    var next = part(el, "next");
    var dotsHost = part(el, "dots");

    var index = 0;
    var cancel = null;
    var timer = null;
    var hovered = false;

    if (viewport.style) viewport.style.overflow = "hidden";
    if (track.style) {
      track.style.display = "flex";
      track.style.flexWrap = "nowrap";
      track.style.willChange = "transform";
    }
    for (var s = 0; s < slides.length; s++) {
      if (slides[s].style) {
        slides[s].style.flex = "0 0 100%";
        slides[s].style.maxWidth = "100%";
      }
    }

    if (typeof el.setAttribute === "function") {
      el.setAttribute("role", "group");
      el.setAttribute("aria-roledescription", "carousel");
      if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", label);
    }

    var dots = [];
    if (dotsHost) {
      // Built rather than authored, because the count is a property of
      // the slides and a hand-authored set would silently disagree with
      // them. createElement and textContent only; nothing here parses a
      // string as markup.
      for (var d = 0; d < slides.length; d++) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.setAttribute("aria-label", "Go to slide " + (d + 1));
        dot.setAttribute(PART, "dot");
        (function (target) {
          dot.addEventListener("click", function () {
            go(target, true);
          });
        })(d);
        dotsHost.appendChild(dot);
        dots.push(dot);
      }
    }

    function measure() {
      return viewport.getBoundingClientRect
        ? viewport.getBoundingClientRect().width || viewport.offsetWidth || 0
        : viewport.offsetWidth || 0;
    }

    function paint(offsetPx) {
      if (track.style) track.style.transform = "translate3d(" + -offsetPx + "px,0,0)";
    }

    function sync() {
      for (var i = 0; i < slides.length; i++) {
        var active = i === index;
        if (typeof slides[i].setAttribute === "function") {
          slides[i].setAttribute("aria-hidden", active ? "false" : "true");
        }
        setState(slides[i], active ? "active" : null);
      }
      for (var j = 0; j < dots.length; j++) {
        dots[j].setAttribute("aria-current", j === index ? "true" : "false");
        setState(dots[j], j === index ? "active" : null);
      }
      if (!loop) {
        if (prev && typeof prev.toggleAttribute === "function") {
          prev.toggleAttribute("disabled", index === 0);
        }
        if (next && typeof next.toggleAttribute === "function") {
          next.toggleAttribute("disabled", index === slides.length - 1);
        }
      }
    }

    function go(target, userDriven) {
      var count = slides.length;
      var wanted = target;
      if (loop) {
        wanted = ((target % count) + count) % count;
      } else {
        wanted = Math.max(0, Math.min(count - 1, target));
      }
      if (wanted === index && userDriven !== true) return;

      var width = measure();
      var fromPx = index * width;
      var toPx = wanted * width;
      index = wanted;

      if (cancel) cancel();
      cancel = tween(duration, ease, function (p) {
        paint(fromPx + (toPx - fromPx) * p);
      });
      sync();
    }

    if (prev) {
      prev.addEventListener("click", function () {
        go(index - 1, true);
        rest();
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        go(index + 1, true);
        rest();
      });
    }

    // Arrow keys, but only while the carousel itself holds focus, so the
    // page's own left and right keys are untouched everywhere else.
    if (typeof el.setAttribute === "function" && !el.getAttribute("tabindex")) {
      el.setAttribute("tabindex", "0");
    }
    el.addEventListener("keydown", function (event) {
      if (event.key === "ArrowLeft") {
        go(index - 1, true);
        rest();
      } else if (event.key === "ArrowRight") {
        go(index + 1, true);
        rest();
      }
    });

    function tick() {
      // Nothing to animate on a background tab, and nothing to animate
      // for an element that is no longer in the document. The second is
      // the one that matters: a CMS list swapping its items would
      // otherwise leave an interval running against a detached tree for
      // as long as the page is open.
      if (!el.isConnected) {
        if (timer !== null) {
          window.clearInterval(timer);
          timer = null;
        }
        return;
      }
      if (hovered || document.hidden) return;
      go(index + 1);
    }

    function rest() {
      if (!interval) return;
      if (timer !== null) window.clearInterval(timer);
      timer = window.setInterval(tick, interval);
    }

    if (pauseHover) {
      el.addEventListener("mouseenter", function () {
        hovered = true;
      });
      el.addEventListener("mouseleave", function () {
        hovered = false;
      });
      el.addEventListener("focusin", function () {
        hovered = true;
      });
      el.addEventListener("focusout", function () {
        hovered = false;
      });
    }

    // Autoplay is opt-in and reduced motion turns it off. Arrows and
    // dots keep working, so the carousel is never inoperable, only
    // never self-moving.
    if (interval > 0 && !reducedMotion()) rest();

    onScroll(function () {
      // Keep the offset correct when the viewport is resized. Cheap,
      // because it shares the one throttled resize subscription.
      if (!cancel) paint(index * measure());
    }, el);

    paint(0);
    sync();
    setState(el, "ready");
  }

  /* ── accordion ─────────────────────────────────────────────────── */

  function initAccordion(root) {
    var items = parts(root, "item");
    if (!items.length) return;

    var multiple = bool(root, "multiple", false);
    var duration = num(root, "duration", 280, 0, 2000);
    var ease = pick(root, "easing", EASING_NAMES, "power2.out");
    var openIndex = num(root, "open", 0, -1, 200);

    var groupId = "bp-acc-" + Math.floor(Math.random() * 1e9).toString(36);
    var entries = [];

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var trigger = part(item, "trigger", root) || null;
      var panel = part(item, "panel", root) || null;
      if (!trigger || !panel) continue;

      // Only mint ids and roles that are missing. Clobbering an id a
      // component already carries breaks anchors and label references
      // that have nothing to do with us.
      var panelId = panel.id || groupId + "-p" + i;
      if (!panel.id) panel.id = panelId;
      var triggerId = trigger.id || groupId + "-t" + i;
      if (!trigger.id) trigger.id = triggerId;

      if (!panel.getAttribute("role")) panel.setAttribute("role", "region");
      panel.setAttribute("aria-labelledby", triggerId);

      // A div acting as a button needs the semantics of one, or the
      // accordion is mouse-only.
      var tag = String(trigger.tagName || "").toLowerCase();
      if (tag !== "button") {
        if (!trigger.getAttribute("role")) trigger.setAttribute("role", "button");
        if (!trigger.getAttribute("tabindex")) trigger.setAttribute("tabindex", "0");
      }
      trigger.setAttribute("aria-controls", panelId);

      entries.push({ item: item, trigger: trigger, panel: panel, open: false, cancel: null });
    }
    if (!entries.length) return;

    function apply(entry, open, animate) {
      entry.open = open;
      entry.trigger.setAttribute("aria-expanded", open ? "true" : "false");
      setState(entry.item, open ? "open" : "closed");

      var panel = entry.panel;
      if (entry.cancel) entry.cancel();

      if (!animate || reducedMotion() || duration === 0) {
        panel.style.height = "";
        panel.style.overflow = "";
        panel.hidden = !open;
        return;
      }

      panel.hidden = false;
      panel.style.overflow = "hidden";
      var full = panel.scrollHeight;
      var from = open ? 0 : full;
      var to = open ? full : 0;

      entry.cancel = tween(
        duration,
        ease,
        function (p) {
          panel.style.height = from + (to - from) * p + "px";
        },
        function () {
          panel.style.height = "";
          panel.style.overflow = "";
          panel.hidden = !open;
          entry.cancel = null;
        }
      );
    }

    function toggle(at) {
      var entry = entries[at];
      var next = !entry.open;
      if (next && !multiple) {
        for (var i = 0; i < entries.length; i++) {
          if (i !== at && entries[i].open) apply(entries[i], false, true);
        }
      }
      apply(entry, next, true);
    }

    for (var e = 0; e < entries.length; e++) {
      (function (at) {
        var entry = entries[at];
        entry.trigger.addEventListener("click", function (event) {
          if (typeof event.preventDefault === "function") event.preventDefault();
          toggle(at);
        });
        entry.trigger.addEventListener("keydown", function (event) {
          if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
            if (typeof event.preventDefault === "function") event.preventDefault();
            toggle(at);
          }
        });
      })(e);
      apply(entries[e], e === openIndex, false);
    }

    setState(root, "ready");
  }

  /* ── tabs ──────────────────────────────────────────────────────── */

  function initTabs(root) {
    var tabs = parts(root, "tab");
    var panels = parts(root, "panel");
    if (!tabs.length || !panels.length) return;

    var groupId = "bp-tabs-" + Math.floor(Math.random() * 1e9).toString(36);
    var manual = pick(root, "activation", ["auto", "manual"], "manual") === "manual";

    // Paired by name, not by position, so reordering the markup in the
    // Designer cannot silently repoint a tab at the wrong panel.
    var byName = {};
    for (var p = 0; p < panels.length; p++) {
      var pname = raw(panels[p], "id");
      if (pname) byName[pname] = panels[p];
    }

    var pairs = [];
    for (var t = 0; t < tabs.length; t++) {
      var want = raw(tabs[t], "for");
      var panel = want && Object.prototype.hasOwnProperty.call(byName, want)
        ? byName[want]
        : panels[t] || null;
      if (!panel) continue;
      pairs.push({ tab: tabs[t], panel: panel });
    }
    if (!pairs.length) return;

    var list = tabs[0].parentElement;
    if (list && typeof list.setAttribute === "function" && !list.getAttribute("role")) {
      list.setAttribute("role", "tablist");
    }

    for (var i = 0; i < pairs.length; i++) {
      var tab = pairs[i].tab;
      var pan = pairs[i].panel;
      var tabId = tab.id || groupId + "-t" + i;
      if (!tab.id) tab.id = tabId;
      var panId = pan.id || groupId + "-p" + i;
      if (!pan.id) pan.id = panId;

      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-controls", panId);
      pan.setAttribute("role", "tabpanel");
      pan.setAttribute("aria-labelledby", tabId);
    }

    function select(at, focus) {
      for (var i = 0; i < pairs.length; i++) {
        var active = i === at;
        pairs[i].tab.setAttribute("aria-selected", active ? "true" : "false");
        pairs[i].tab.setAttribute("tabindex", active ? "0" : "-1");
        setState(pairs[i].tab, active ? "active" : null);
        pairs[i].panel.hidden = !active;
        setState(pairs[i].panel, active ? "active" : null);
      }
      if (focus && pairs[at] && typeof pairs[at].tab.focus === "function") {
        pairs[at].tab.focus();
      }
    }

    for (var k = 0; k < pairs.length; k++) {
      (function (at) {
        pairs[at].tab.addEventListener("click", function (event) {
          if (typeof event.preventDefault === "function") event.preventDefault();
          select(at, false);
        });
        pairs[at].tab.addEventListener("keydown", function (event) {
          var last = pairs.length - 1;
          var to = null;
          if (event.key === "ArrowRight") to = at === last ? 0 : at + 1;
          else if (event.key === "ArrowLeft") to = at === 0 ? last : at - 1;
          else if (event.key === "Home") to = 0;
          else if (event.key === "End") to = last;
          else if (event.key === "Enter" || event.key === " ") {
            if (typeof event.preventDefault === "function") event.preventDefault();
            select(at, false);
            return;
          }
          if (to === null) return;
          if (typeof event.preventDefault === "function") event.preventDefault();
          if (manual) {
            if (typeof pairs[to].tab.focus === "function") pairs[to].tab.focus();
          } else {
            select(to, true);
          }
        });
      })(k);
    }

    select(0, false);
    setState(root, "ready");
  }

  /* ── modal ─────────────────────────────────────────────────────
   *
   * Split into two behaviours because an opener almost never lives
   * inside the dialog it opens, and the part system is deliberately
   * ownership-scoped. So the dialog is data-backpack="modal" with a
   * data-backpack-id, and each opener is data-backpack="modal-open"
   * with a matching data-backpack-for, wherever it happens to sit.
   * ───────────────────────────────────────────────────────────────── */

  /**
   * Named dialogs, keyed by their data-backpack-id.
   *
   * Prototype-less on purpose. As a plain object, a component whose id is
   * literally `__proto__` would not create an own key at all: the
   * assignment replaces this object's prototype instead, so that modal
   * becomes permanently unreachable and the registry grows a prototype
   * nobody put there. Object.create(null) makes `__proto__` an ordinary
   * string key like any other.
   */
  var modals = Object.create(null);

  /**
   * The page's scroll lock, shared by every modal and reference counted.
   *
   * Each modal used to store the body's previous overflow on itself and
   * restore it on close. With two open at once, the inner one saved
   * "hidden" from the outer one, and closing the outer first left the
   * page permanently unscrollable. Counting means the value is captured
   * once, when the count goes from zero, and restored once, when it comes
   * back to zero, whatever order they close in.
   *
   * The previous value is saved rather than assumed empty, because plenty
   * of sites set overflow on the body themselves and clearing it would
   * change their layout.
   */
  var lockDepth = 0;
  var lockPrevious = "";

  function lockScroll() {
    if (lockDepth === 0) {
      lockPrevious = document.body.style.overflow || "";
      document.body.style.overflow = "hidden";
    }
    lockDepth++;
  }

  function unlockScroll() {
    if (lockDepth === 0) return;
    lockDepth--;
    if (lockDepth === 0) document.body.style.overflow = lockPrevious;
  }

  var FOCUSABLE =
    'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"]),video[controls],audio[controls],summary';

  function initModal(root) {
    var name = ident(root, "id");
    var closeBackdrop = bool(root, "close-backdrop", true);
    var closeEscape = bool(root, "close-escape", true);
    var duration = num(root, "duration", 200, 0, 2000);
    var ease = pick(root, "easing", EASING_NAMES, "power2.out");
    var label = text(root, "label", "Dialog", 60);

    var dialog = part(root, "dialog") || root;
    var closers = parts(root, "close");
    var opener = null;
    var lastFocus = null;
    var open = false;

    if (typeof root.setAttribute === "function") {
      root.setAttribute("role", "dialog");
      root.setAttribute("aria-modal", "true");
      if (!root.getAttribute("aria-label")) root.setAttribute("aria-label", label);
    }
    root.hidden = true;
    setState(root, "closed");

    function focusables() {
      var out = [];
      var found;
      try {
        found = dialog.querySelectorAll(FOCUSABLE);
      } catch (err) {
        return out;
      }
      for (var i = 0; i < found.length; i++) {
        // Skip anything hidden; a trap that cycles through invisible
        // controls is worse than no trap.
        if (found[i].offsetParent !== null || found[i] === document.activeElement) {
          out.push(found[i]);
        }
      }
      return out;
    }

    function onKey(event) {
      if (!open) return;
      if (closeEscape && event.key === "Escape") {
        if (typeof event.preventDefault === "function") event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        if (typeof event.preventDefault === "function") event.preventDefault();
        if (typeof last.focus === "function") last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        if (typeof event.preventDefault === "function") event.preventDefault();
        if (typeof first.focus === "function") first.focus();
      }
    }

    function show(from) {
      if (open) return;
      open = true;
      opener = from || null;
      lastFocus = document.activeElement;
      root.hidden = false;
      setState(root, "open");

      lockScroll();

      if (root.style) root.style.opacity = "0";
      tween(
        duration,
        ease,
        function (p) {
          if (root.style) root.style.opacity = String(p);
        },
        function () {
          if (root.style) root.style.opacity = "";
        }
      );

      var items = focusables();
      var target = items.length ? items[0] : dialog;
      if (target && typeof target.focus === "function") {
        if (target === dialog && typeof dialog.setAttribute === "function") {
          if (!dialog.getAttribute("tabindex")) dialog.setAttribute("tabindex", "-1");
        }
        target.focus();
      }
      document.addEventListener("keydown", onKey);
    }

    function close() {
      if (!open) return;
      open = false;
      document.removeEventListener("keydown", onKey);
      unlockScroll();

      tween(
        duration,
        ease,
        function (p) {
          if (root.style) root.style.opacity = String(1 - p);
        },
        function () {
          if (root.style) root.style.opacity = "";
          root.hidden = true;
          setState(root, "closed");
          var back = opener || lastFocus;
          if (back && typeof back.focus === "function") back.focus();
        }
      );
    }

    for (var c = 0; c < closers.length; c++) {
      closers[c].addEventListener("click", function (event) {
        if (typeof event.preventDefault === "function") event.preventDefault();
        close();
      });
    }

    if (closeBackdrop && dialog !== root) {
      root.addEventListener("click", function (event) {
        if (event.target === root) close();
      });
    }

    if (name) modals[name] = { show: show, close: close };

    /* NOTHING OPENS THIS DIALOG EXCEPT A PERSON.
     *
     * A previous revision could open a modal on a timer, at a scroll
     * depth, or on exit intent. Webflow's Marketplace Guidelines say, in
     * as many words: "Popups, pop-unders, and unsolicited windows must be
     * triggered only by direct user interaction — never on load or on a
     * timer", and "Every state-changing action must be traceable to a
     * direct user interaction event, not fired on load or on a timer."
     *
     * So there is no timer, no scroll subscription and no mouseleave
     * listener here, and `show` is reachable from exactly two places: a
     * click or key press on a modal-open element, and a close control
     * inside the dialog. If a future edit adds a caller, it has to add it
     * somewhere a reviewer can see. */

    // Unnamed modals are still operable from inside; they simply cannot
    // be opened by a remote trigger.
    root.__backpackModal = { show: show, close: close };
  }

  function initModalOpen(el) {
    var name = ident(el, "for");
    if (typeof el.setAttribute === "function") {
      var tag = String(el.tagName || "").toLowerCase();
      if (tag !== "button" && tag !== "a") {
        if (!el.getAttribute("role")) el.setAttribute("role", "button");
        if (!el.getAttribute("tabindex")) el.setAttribute("tabindex", "0");
      }
      el.setAttribute("aria-haspopup", "dialog");
    }

    function fire(event) {
      if (typeof event.preventDefault === "function") event.preventDefault();
      // Resolved at click time, not at init, because the dialog may be
      // initialised after its opener in document order.
      var target = name && Object.prototype.hasOwnProperty.call(modals, name)
        ? modals[name]
        : null;
      if (target) target.show(el);
    }

    el.addEventListener("click", fire);
    el.addEventListener("keydown", function (event) {
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") fire(event);
    });
  }

  /* ── sticky ────────────────────────────────────────────────────
   *
   * position: sticky is CSS and needs no help. What needs script is
   * knowing WHEN it is stuck, so the component's own CSS can respond.
   * The runtime sets the offset and flips a state attribute, and does
   * no styling of its own beyond that.
   * ───────────────────────────────────────────────────────────────── */

  function initSticky(el) {
    var offset = num(el, "offset", 0, -400, 400);
    var edge = pick(el, "edge", ["top", "bottom"], "top");

    if (el.style) {
      el.style.position = "sticky";
      el.style[edge] = offset + "px";
    }

    var stuck = false;
    onScroll(function () {
      if (!el.getBoundingClientRect) return;
      var box = el.getBoundingClientRect();
      var isStuck =
        edge === "top"
          ? box.top <= offset + 0.5
          : box.bottom >= viewportHeight() - offset - 0.5;
      if (isStuck === stuck) return;
      stuck = isStuck;
      setState(el, stuck ? "stuck" : null);
    }, el);
  }

  /* ── parallax ──────────────────────────────────────────────────── */

  function initParallax(el) {
    var speed = num(el, "speed", 0.2, -1, 1);
    var axis = pick(el, "axis", ["y", "x"], "y");
    var max = num(el, "max", 200, 0, 800);

    if (reducedMotion() || speed === 0) {
      setState(el, "static");
      return;
    }
    if (el.style) el.style.willChange = "transform";

    onScroll(function () {
      if (!el.getBoundingClientRect) return;
      var box = el.getBoundingClientRect();
      var vh = viewportHeight();
      if (box.bottom < -vh || box.top > vh * 2) return; // far offscreen, skip
      // Zero at the moment the element's centre crosses the viewport
      // centre, so the effect is symmetric on the way in and out and
      // never leaves the element displaced at rest.
      var centre = box.top + box.height / 2;
      var delta = (centre - vh / 2) * speed;
      if (delta > max) delta = max;
      if (delta < -max) delta = -max;
      el.style.transform =
        axis === "y"
          ? "translate3d(0," + delta + "px,0)"
          : "translate3d(" + delta + "px,0,0)";
    }, el);
  }

  /* ── scrollprogress ────────────────────────────────────────────── */

  function initScrollProgress(el) {
    var axis = pick(el, "axis", ["x", "y"], "x");
    var name = ident(el, "for");

    if (typeof el.setAttribute === "function") {
      el.setAttribute("role", "progressbar");
      el.setAttribute("aria-hidden", "true");
    }
    if (el.style) el.style.transformOrigin = axis === "x" ? "left center" : "center top";

    function subject() {
      return byId(name);
    }

    onScroll(function () {
      var progress;
      var target = subject();
      if (target && target.getBoundingClientRect) {
        var box = target.getBoundingClientRect();
        var span = box.height - viewportHeight();
        progress = span <= 0 ? (box.top <= 0 ? 1 : 0) : clamp01(-box.top / span);
      } else {
        var doc = document.documentElement;
        var scrollable = doc.scrollHeight - viewportHeight();
        progress = scrollable <= 0 ? 0 : clamp01((window.pageYOffset || doc.scrollTop || 0) / scrollable);
      }
      if (el.style) {
        el.style.setProperty("--backpack-progress", String(progress));
        el.style.transform =
          axis === "x" ? "scaleX(" + progress + ")" : "scaleY(" + progress + ")";
      }
      if (typeof el.setAttribute === "function") {
        el.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
      }
    }, el);
  }

  /* ── pointer ───────────────────────────────────────────────────
   *
   * Publishes the cursor's position over the element as two custom
   * properties in the range 0 to 1, and optionally tilts. Everything
   * expressive is left to the component's own CSS reading those
   * properties, which keeps this small and keeps the design space open.
   * ───────────────────────────────────────────────────────────────── */

  function initPointer(el) {
    var effect = pick(el, "effect", ["track", "tilt"], "track");
    var strength = num(el, "strength", 8, 0, 30);
    var reset = bool(el, "reset", true);

    if (reducedMotion()) {
      setState(el, "static");
      return;
    }
    // Touch devices fire a single synthetic move on tap, which reads as
    // a jolt. Pointer effects are for devices that actually hover.
    var hoverable = true;
    try {
      hoverable =
        typeof window.matchMedia !== "function" ||
        window.matchMedia("(hover: hover)").matches;
    } catch (err) {
      hoverable = true;
    }
    if (!hoverable) return;

    // A mousemove fires far more often than a frame renders. Measuring
    // and writing styles on every one of them is a read-write-read cycle
    // per event, which is the classic way to make a card feel worse the
    // more it is used. Coordinates are recorded on the event and applied
    // at most once per frame.
    var pending = false;
    var lastX = 0;
    var lastY = 0;

    function move(event) {
      lastX = event.clientX;
      lastY = event.clientY;
      if (pending) return;
      pending = true;
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(apply);
      } else {
        apply();
      }
    }

    function apply() {
      pending = false;
      if (!el.isConnected || !el.getBoundingClientRect) return;
      var box = el.getBoundingClientRect();
      if (!box.width || !box.height) return;
      var x = clamp01((lastX - box.left) / box.width);
      var y = clamp01((lastY - box.top) / box.height);
      if (!el.style) return;
      el.style.setProperty("--backpack-px", String(x));
      el.style.setProperty("--backpack-py", String(y));
      if (effect === "tilt") {
        el.style.transform =
          "perspective(900px) rotateX(" +
          (0.5 - y) * strength +
          "deg) rotateY(" +
          (x - 0.5) * strength +
          "deg)";
      }
    }

    function leave() {
      if (!reset || !el.style) return;
      el.style.setProperty("--backpack-px", "0.5");
      el.style.setProperty("--backpack-py", "0.5");
      if (effect === "tilt") el.style.transform = "";
    }

    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    leave();
    setState(el, "ready");
  }

  /* ── media ─────────────────────────────────────────────────────── */

  function initMedia(el) {
    var tag = String(el.tagName || "").toLowerCase();
    if (tag !== "video" && tag !== "audio") return;

    var pauseOffscreen = bool(el, "pause-offscreen", true);
    var threshold = num(el, "threshold", 0.25, 0, 1);

    // AUDIO NEVER STARTS ITSELF (Webflow review, submission 3, finding 3).
    //
    // This clamp used to sit inside `if (tag === "video")`, and `initMedia`
    // accepts both tags, so an <audio data-backpack="media"> element was
    // handed to `attempt(true)` unmuted on scroll-into-view. In practice
    // browsers usually block that, and the .catch below swallowed the
    // rejection, so it never looked like a bug. "Usually" is not the
    // standard: Chrome's Media Engagement Index and an autoplay permission
    // policy both let an unmuted element through on a domain the visitor
    // has used before. Sound starting by itself on a customer's site is
    // exactly the outcome the reviewer asked us to rule out.
    //
    // Muting audio would make autoplay pointless, so audio simply does not
    // autoplay. It still gets pause-offscreen, and its controls still work,
    // so a visitor who presses play gets what they asked for.
    var mayAutoplay = tag === "video";
    if (tag === "video") {
      el.muted = true;
      if (typeof el.setAttribute === "function") {
        el.setAttribute("muted", "");
        el.setAttribute("playsinline", "");
      }
    }

    function attempt(play) {
      try {
        if (play) {
          var result = el.play();
          if (result && typeof result.catch === "function") {
            result.catch(function () {
              /* blocked by policy; the controls still work */
            });
          }
        } else if (typeof el.pause === "function") {
          el.pause();
        }
      } catch (err) {
        /* a media element that refuses is not an error worth throwing */
      }
    }

    if (reducedMotion()) {
      // Do not start anything. Leave whatever controls exist alone.
      setState(el, "static");
      return;
    }

    watchViewport(
      el,
      threshold,
      function () {
        // Audio is observed only so it can be PAUSED when it scrolls away.
        // Entering the viewport starts nothing.
        if (mayAutoplay) attempt(true);
      },
      false,
      pauseOffscreen
        ? function () {
            attempt(false);
          }
        : null
    );
    setState(el, mayAutoplay ? "ready" : "manual");
  }


  /* ── countdown ─────────────────────────────────────────────────
   *
   * Counts down to a fixed moment. No network: the target is a date in
   * the markup and the browser's own clock does the rest, so nothing is
   * fetched and nothing about the visitor is read beyond what time it is
   * where they are.
   * ───────────────────────────────────────────────────────────────── */

  function initCountdown(root) {
    var target = Date.parse(text(root, "to", "", 32));
    if (!isFinite(target)) return;

    var pad = bool(root, "pad", true);
    var slots = [
      ["days", 86400000],
      ["hours", 3600000],
      ["minutes", 60000],
      ["seconds", 1000],
    ];
    var fields = [];
    for (var i = 0; i < slots.length; i++) {
      var node = part(root, slots[i][0]);
      if (node) fields.push({ el: node, ms: slots[i][1], name: slots[i][0] });
    }
    var expired = part(root, "expired");
    if (!fields.length && !expired) return;

    if (expired) expired.hidden = true;

    var timer = null;

    function stop() {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function render() {
      if (!root.isConnected) {
        stop();
        return;
      }
      var left = target - new Date().getTime();
      var done = left <= 0;
      if (done) left = 0;

      var remaining = left;
      for (var i = 0; i < fields.length; i++) {
        var unit = fields[i];
        var value = Math.floor(remaining / unit.ms);
        remaining -= value * unit.ms;
        var out = String(value);
        // Two digits everywhere except days, which can legitimately be
        // three and look wrong zero-padded to two.
        if (pad && unit.name !== "days" && out.length < 2) out = "0" + out;
        unit.el.textContent = out;
      }

      if (done) {
        stop();
        setState(root, "expired");
        if (expired) expired.hidden = false;
        for (var j = 0; j < fields.length; j++) {
          var owner = fields[j].el.parentElement;
          if (owner && owner !== root) owner.hidden = true;
        }
      }
    }

    render();
    if (Date.parse(text(root, "to", "", 32)) > new Date().getTime()) {
      setState(root, "running");
      timer = window.setInterval(render, 1000);
    }
  }

  /* ── scrollspy ─────────────────────────────────────────────────
   *
   * Marks the link whose section is currently in view. Links are parts
   * of this nav; sections are found by literal id comparison, the same
   * mechanism the progress bar uses. A link pointing at nothing simply
   * never becomes active.
   * ───────────────────────────────────────────────────────────────── */

  function initScrollSpy(root) {
    var links = parts(root, "link");
    if (!links.length) return;

    var offset = num(root, "offset", 80, -400, 400);
    var pairs = [];
    for (var i = 0; i < links.length; i++) {
      var wanted = ident(links[i], "for");
      if (!wanted) continue;
      pairs.push({ link: links[i], name: wanted });
    }
    if (!pairs.length) return;

    var current = -1;

    onScroll(function () {
      var best = -1;
      var bestTop = -Infinity;
      for (var i = 0; i < pairs.length; i++) {
        // Resolved every pass rather than cached, because a CMS list can
        // replace its sections after this ran. It is a presence query and
        // a string compare, inside the one throttled frame.
        var section = byId(pairs[i].name);
        if (!section || !section.getBoundingClientRect) continue;
        var top = section.getBoundingClientRect().top - offset;
        // The last section whose top has passed the offset line.
        if (top <= 0 && top > bestTop) {
          bestTop = top;
          best = i;
        }
      }
      if (best === current) return;
      current = best;
      for (var j = 0; j < pairs.length; j++) {
        var active = j === best;
        setState(pairs[j].link, active ? "active" : null);
        if (typeof pairs[j].link.setAttribute === "function") {
          if (active) pairs[j].link.setAttribute("aria-current", "true");
          else pairs[j].link.removeAttribute("aria-current");
        }
      }
    }, root);

    setState(root, "ready");
  }

  /* ── compare ───────────────────────────────────────────────────
   *
   * A draggable divider over two stacked images. Entirely self-contained:
   * it clips its own layer and moves its own handle, and touches nothing
   * outside its own root.
   * ───────────────────────────────────────────────────────────────── */

  function initCompare(root) {
    var before = part(root, "before");
    var handle = part(root, "handle");
    if (!before || !handle) return;

    var axis = pick(root, "axis", ["x", "y"], "x");
    var value = num(root, "start", 50, 0, 100);

    if (root.style) {
      root.style.position = root.style.position || "relative";
      root.style.touchAction = axis === "x" ? "pan-y" : "pan-x";
    }

    if (typeof handle.setAttribute === "function") {
      handle.setAttribute("role", "slider");
      handle.setAttribute("aria-valuemin", "0");
      handle.setAttribute("aria-valuemax", "100");
      if (!handle.getAttribute("aria-label")) {
        handle.setAttribute("aria-label", "Reveal the before image");
      }
      if (!handle.getAttribute("tabindex")) handle.setAttribute("tabindex", "0");
    }

    function paint() {
      if (before.style) {
        before.style.clipPath =
          axis === "x"
            ? "inset(0 " + (100 - value) + "% 0 0)"
            : "inset(0 0 " + (100 - value) + "% 0)";
      }
      if (handle.style) {
        if (axis === "x") handle.style.left = value + "%";
        else handle.style.top = value + "%";
      }
      if (typeof handle.setAttribute === "function") {
        handle.setAttribute("aria-valuenow", String(Math.round(value)));
      }
    }

    function setFromPoint(clientX, clientY) {
      if (!root.getBoundingClientRect) return;
      var box = root.getBoundingClientRect();
      if (!box.width || !box.height) return;
      value =
        axis === "x"
          ? clamp01((clientX - box.left) / box.width) * 100
          : clamp01((clientY - box.top) / box.height) * 100;
      paint();
    }

    var dragging = false;
    var pending = false;
    var lastX = 0;
    var lastY = 0;

    function apply() {
      pending = false;
      if (root.isConnected) setFromPoint(lastX, lastY);
    }

    function onMove(event) {
      if (!dragging) return;
      lastX = event.clientX;
      lastY = event.clientY;
      if (pending) return;
      pending = true;
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(apply);
      } else {
        apply();
      }
    }

    function stop() {
      if (!dragging) return;
      dragging = false;
      setState(root, "ready");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", stop);
    }

    handle.addEventListener("mousedown", function (event) {
      if (typeof event.preventDefault === "function") event.preventDefault();
      dragging = true;
      setState(root, "dragging");
      // On document only while a drag is in progress, and removed the
      // moment it ends, because a pointer that leaves the element mid
      // drag still belongs to this handle.
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", stop);
    });

    root.addEventListener("click", function (event) {
      if (event.target === handle) return;
      setFromPoint(event.clientX, event.clientY);
    });

    handle.addEventListener("keydown", function (event) {
      var step = event.shiftKey ? 10 : 2;
      var next = null;
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = value - step;
      else if (event.key === "ArrowRight" || event.key === "ArrowDown") next = value + step;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = 100;
      if (next === null) return;
      if (typeof event.preventDefault === "function") event.preventDefault();
      value = Math.max(0, Math.min(100, next));
      paint();
    });

    paint();
    setState(root, "ready");
  }


  /* ── toggle ────────────────────────────────────────────────────
   *
   * A two-state disclosure. The trigger carries the behaviour and names
   * its target; the target is any Backpack-owned element with a matching
   * id. Mobile menus, drawers, read-more panels and simple dropdowns are
   * all this one primitive plus CSS.
   *
   * The state lives on the TARGET as data-backpack-state, which is a
   * runtime-owned attribute a payload cannot set, so the starting state
   * is decided here rather than trusted from markup. It always starts
   * closed: a disclosure that starts open is just content.
   * ───────────────────────────────────────────────────────────────── */

  function initToggle(el) {
    var name = ident(el, "for");
    if (!name) return;

    // Resolved once here to set up ARIA, and again on each click, because
    // a CMS list can replace the target after this ran.
    var target = byId(name);
    if (!target) return;

    var tag = String(el.tagName || "").toLowerCase();
    if (typeof el.setAttribute === "function") {
      if (tag !== "button" && tag !== "a") {
        if (!el.getAttribute("role")) el.setAttribute("role", "button");
        if (!el.getAttribute("tabindex")) el.setAttribute("tabindex", "0");
      }
      el.setAttribute("aria-expanded", "false");
      // Only when the target already has an id of its own. Minting one
      // would mean writing an id onto an element this behaviour does not
      // own, and aria-controls is optional.
      if (target.id) el.setAttribute("aria-controls", target.id);
    }
    setState(target, "closed");

    function flip(event) {
      if (typeof event.preventDefault === "function") event.preventDefault();
      var live = byId(name);
      if (!live) return;
      var open = live.getAttribute(STATE) === "open";
      setState(live, open ? "closed" : "open");
      if (typeof el.setAttribute === "function") {
        el.setAttribute("aria-expanded", open ? "false" : "true");
      }
    }

    el.addEventListener("click", flip);
    el.addEventListener("keydown", function (event) {
      if (tag === "button" || tag === "a") return;
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") flip(event);
    });
  }

  /* ── inview ────────────────────────────────────────────────────
   *
   * Publishes whether the element is in the viewport and does nothing
   * else. No animation, no styling, no transform: generated CSS reacts to
   * data-backpack-inview.
   *
   * This exists so a new visual effect does not need a new reviewed
   * behaviour every time. It is the widest thing in the vocabulary and it
   * is still finite, because the only thing configuration can change is
   * when the attribute flips.
   * ───────────────────────────────────────────────────────────────── */

  var INVIEW = "data-backpack-inview";

  function initInView(el) {
    var threshold = num(el, "threshold", 0.25, 0, 1);
    var once = bool(el, "once", false);

    function set(value) {
      if (typeof el.setAttribute === "function") el.setAttribute(INVIEW, value ? "true" : "false");
    }

    if (typeof window.IntersectionObserver !== "function") {
      // No observer means no way to know. Report visible rather than
      // hidden, because CSS keyed to this attribute is far more likely to
      // reveal on true than to hide on it, and content that never appears
      // is the failure that matters.
      set(true);
      return;
    }

    set(false);
    watchViewport(
      el,
      threshold,
      function () {
        set(true);
      },
      once,
      once
        ? null
        : function () {
            set(false);
          }
    );
  }

  /* ── scrollstate ───────────────────────────────────────────────
   *
   * Flips between "before" and "after" once the page has scrolled past a
   * pixel threshold. Shrinking headers, nav backgrounds, back-to-top
   * buttons and persistent CTAs are all this plus CSS.
   *
   * Rides the one shared throttled scroll subscription; it adds no
   * listener of its own however many are on the page.
   * ───────────────────────────────────────────────────────────────── */

  function initScrollState(el) {
    var threshold = num(el, "threshold", 100, 0, 20000);
    var passed = null;

    onScroll(function () {
      var doc = document.documentElement;
      var y = window.pageYOffset || doc.scrollTop || 0;
      var now = y >= threshold;
      if (now === passed) return;
      passed = now;
      setState(el, now ? "after" : "before");
    }, el);
  }

  /* ── tooltip ───────────────────────────────────────────────────
   *
   * Shows a small Backpack-owned element while the trigger is hovered,
   * focused or clicked. Like inview and scrollstate it publishes state
   * and leaves presentation to CSS: it sets data-backpack-state and
   * echoes the placement, and it never writes a coordinate.
   *
   * That is deliberate. Positioning from script means measuring layout
   * and fighting whatever the component's own CSS already says, and a
   * tooltip that CSS positions is one fewer thing that can be wrong on a
   * site we have never seen.
   * ───────────────────────────────────────────────────────────────── */

  function initTooltip(el) {
    var name = ident(el, "for");
    if (!name) return;
    var tip = byId(name);
    if (!tip) return;

    var placement = pick(el, "placement", ["top", "right", "bottom", "left"], "top");
    var delay = num(el, "delay", 150, 0, 2000);
    var mode = pick(el, "trigger", ["hover", "focus", "hover-focus", "click"], "hover-focus");

    var tipId = tip.id || "bp-tip-" + Math.floor(Math.random() * 1e9).toString(36);
    if (!tip.id) tip.id = tipId;
    if (typeof tip.setAttribute === "function") {
      if (!tip.getAttribute("role")) tip.setAttribute("role", "tooltip");
      tip.setAttribute(OPT + "placement", placement);
    }
    if (typeof el.setAttribute === "function") {
      // Describes rather than labels: the trigger keeps its own name and
      // the tooltip is read as supplementary.
      el.setAttribute("aria-describedby", tipId);
      var tag = String(el.tagName || "").toLowerCase();
      if (mode === "click" && tag !== "button" && tag !== "a") {
        if (!el.getAttribute("role")) el.setAttribute("role", "button");
        if (!el.getAttribute("tabindex")) el.setAttribute("tabindex", "0");
      } else if (!el.getAttribute("tabindex") && tag !== "button" && tag !== "a") {
        // Keyboard users must be able to reach it, or the tooltip is
        // mouse-only.
        el.setAttribute("tabindex", "0");
      }
    }
    tip.hidden = true;
    setState(tip, "closed");

    var timer = null;
    var shown = false;

    function show() {
      if (shown) return;
      shown = true;
      tip.hidden = false;
      setState(tip, "open");
    }

    function hide() {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      if (!shown) return;
      shown = false;
      tip.hidden = true;
      setState(tip, "closed");
    }

    function showAfterDelay() {
      if (shown || timer !== null) return;
      if (delay <= 0) {
        show();
        return;
      }
      timer = window.setTimeout(function () {
        timer = null;
        show();
      }, delay);
    }

    if (mode === "click") {
      el.addEventListener("click", function (event) {
        if (typeof event.preventDefault === "function") event.preventDefault();
        if (shown) hide();
        else show();
      });
      el.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
          if (typeof event.preventDefault === "function") event.preventDefault();
          if (shown) hide();
          else show();
        }
      });
    } else {
      if (mode === "hover" || mode === "hover-focus") {
        el.addEventListener("mouseenter", showAfterDelay);
        el.addEventListener("mouseleave", hide);
      }
      if (mode === "focus" || mode === "hover-focus") {
        el.addEventListener("focus", show);
        el.addEventListener("blur", hide);
      }
    }

    // Escape dismisses, which is the one thing every dismissible overlay
    // owes a keyboard user. Bound on the trigger rather than on document,
    // because a tooltip does not take focus and the trigger still has it.
    el.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && shown) hide();
    });
  }


  /* ═════════════════════════════════════════════════════════════════
   * CHOREOGRAPHY
   *
   * Fixed, named sequences. Configuration chooses timings and distances;
   * it never chooses order, targets or properties. There is no step list,
   * no action name and no target selector anywhere below: each of these
   * functions IS its choreography, written out, reviewable as code.
   * ═════════════════════════════════════════════════════════════════ */

  /* ── hero-sequence ─────────────────────────────────────────────
   *
   * The five parts of a hero, in the one order a hero reads in. Missing
   * parts are skipped and the sequence closes up behind them, so a hero
   * with no eyebrow simply starts at the heading.
   *
   * The order is a literal array here and cannot be configured. That is
   * the whole point: a component asks for "a hero sequence", not for
   * "these elements in this order with these overlaps".
   * ───────────────────────────────────────────────────────────────── */

  var HERO_ORDER = ["eyebrow", "heading", "body", "actions", "visual"];

  function initHeroSequence(root) {
    var duration = num(root, "duration", 700, 0, 6000);
    var stagger = num(root, "stagger", 120, 0, 2000);
    var delay = num(root, "delay", 0, 0, 6000);
    var distance = num(root, "distance", 32, 0, 400);
    var direction = pick(root, "direction", ["up", "down", "left", "right", "none"], "up");
    var ease = pick(root, "easing", EASING_NAMES, "power2.out");

    var ordered = [];
    for (var i = 0; i < HERO_ORDER.length; i++) {
      var found = part(root, HERO_ORDER[i]);
      if (found) ordered.push(found);
    }
    if (!ordered.length) return;

    var off = offsetFor(direction, distance);

    if (reducedMotion()) {
      for (var r = 0; r < ordered.length; r++) setState(ordered[r], "revealed");
      setState(root, "ready");
      return;
    }

    for (var j = 0; j < ordered.length; j++) {
      if (!ordered[j].style) continue;
      setOpacity(ordered[j], 0);
      if (off[0] || off[1]) {
        ordered[j].style.transform = "translate3d(" + off[0] + "px," + off[1] + "px,0)";
      }
      ordered[j].style.willChange = "opacity, transform";
    }

    function play(target, index) {
      var run = function () {
        tween(
          duration,
          ease,
          function (p) {
            if (!target.style) return;
            setOpacity(target, p);
            if (off[0] || off[1]) {
              target.style.transform =
                "translate3d(" + off[0] * (1 - p) + "px," + off[1] * (1 - p) + "px,0)";
            }
          },
          function () {
            if (target.style) {
              target.style.opacity = "";
              target.style.transform = "";
              target.style.willChange = "";
            }
            setState(target, "revealed");
          }
        );
      };
      var wait = delay + stagger * index;
      if (wait > 0) window.setTimeout(run, wait);
      else run();
    }

    // A hero is above the fold by definition, so it plays on load rather
    // than on intersection. Nothing about it is a popup or a window.
    for (var k = 0; k < ordered.length; k++) play(ordered[k], k);
    setState(root, "ready");
  }

  /* ── split-reveal ──────────────────────────────────────────────
   *
   * Wraps each word of its own text in an owned span and staggers them
   * in. WORDS ONLY. Line splitting means measuring where the browser
   * actually broke the text and redoing it on every resize and webfont
   * load, which is a class of fragility this runtime does not need.
   *
   * The accessible text is preserved by putting the original string on
   * the root as aria-label and hiding the fragments from assistive
   * technology, so a screen reader reads one sentence rather than eleven
   * disconnected words.
   * ───────────────────────────────────────────────────────────────── */

  function initSplitReveal(el) {
    // Text only. An element containing markup, a form control, or
    // anything editable is refused rather than rewritten: this is the
    // only behaviour in the runtime that replaces content, and it does it
    // exactly once, on plain text it can see in full.
    if (el.children && el.children.length) return;
    var original = String(el.textContent || "");
    if (!original.trim()) return;
    if (original.length > 600) return;

    var duration = num(el, "duration", 550, 0, 6000);
    var stagger = num(el, "stagger", 45, 0, 500);
    var delay = num(el, "delay", 0, 0, 6000);
    var distance = num(el, "distance", 18, 0, 400);
    var direction = pick(el, "direction", ["up", "down", "left", "right", "none"], "up");
    var ease = pick(el, "easing", EASING_NAMES, "power2.out");
    var threshold = num(el, "threshold", 0.2, 0, 1);
    var once = bool(el, "once", true);
    var fade = bool(el, "fade", true);

    if (typeof el.setAttribute === "function" && !el.getAttribute("aria-label")) {
      el.setAttribute("aria-label", original.replace(/\s+/g, " ").trim());
    }

    // Split on whitespace runs, keeping the separators so the original
    // spacing survives. No configurable pattern: this expression is the
    // whole tokenizer and it is not reachable from configuration.
    var pieces = original.split(/(\s+)/);
    var spans = [];
    while (el.firstChild) el.removeChild(el.firstChild);

    for (var i = 0; i < pieces.length; i++) {
      var piece = pieces[i];
      if (piece === "") continue;
      if (/^\s+$/.test(piece)) {
        el.appendChild(document.createTextNode(piece));
        continue;
      }
      var span = document.createElement("span");
      span.textContent = piece;
      span.setAttribute(PART, "word");
      span.setAttribute("aria-hidden", "true");
      span.style.display = "inline-block";
      span.style.willChange = "opacity, transform";
      el.appendChild(span);
      spans.push(span);
    }
    if (!spans.length) return;

    var off = offsetFor(direction, distance);

    if (reducedMotion()) {
      for (var d = 0; d < spans.length; d++) {
        spans[d].style.willChange = "";
        setState(spans[d], "revealed");
      }
      return;
    }

    for (var h = 0; h < spans.length; h++) {
      if (fade) setOpacity(spans[h], 0);
      if (off[0] || off[1]) {
        spans[h].style.transform = "translate3d(" + off[0] + "px," + off[1] + "px,0)";
      }
    }

    onEnter(
      el,
      threshold,
      function () {
        for (var i = 0; i < spans.length; i++) {
          (function (span, index) {
            var run = function () {
              tween(
                duration,
                ease,
                function (p) {
                  if (fade) setOpacity(span, p);
                  if (off[0] || off[1]) {
                    span.style.transform =
                      "translate3d(" + off[0] * (1 - p) + "px," + off[1] * (1 - p) + "px,0)";
                  }
                },
                function () {
                  span.style.opacity = "";
                  span.style.transform = "";
                  span.style.willChange = "";
                  setState(span, "revealed");
                }
              );
            };
            var wait = delay + stagger * index;
            if (wait > 0) window.setTimeout(run, wait);
            else run();
          })(spans[i], i);
        }
      },
      once
    );
  }

  /* ── mask-reveal ───────────────────────────────────────────────
   *
   * Wipes its own content in from one of five fixed directions using
   * clip-path inset. The five insets are written out below; no clip-path
   * value is ever accepted from configuration.
   * ───────────────────────────────────────────────────────────────── */

  var MASKS = {
    left: "inset(0 100% 0 0)",
    right: "inset(0 0 0 100%)",
    up: "inset(100% 0 0 0)",
    down: "inset(0 0 100% 0)",
    center: "inset(50% 50% 50% 50%)",
  };

  function initMaskReveal(el) {
    var direction = pick(el, "direction", ["left", "right", "up", "down", "center"], "left");
    var duration = num(el, "duration", 800, 0, 6000);
    var delay = num(el, "delay", 0, 0, 6000);
    var ease = pick(el, "easing", EASING_NAMES, "power3.out");
    var threshold = num(el, "threshold", 0.2, 0, 1);
    var once = bool(el, "once", true);

    if (reducedMotion()) {
      setState(el, "revealed");
      return;
    }
    if (!el.style) return;

    var from = MASKS[direction] || MASKS.left;
    var hidden = 100;
    el.style.clipPath = from;
    el.style.willChange = "clip-path";

    function frame(p) {
      var amount = hidden * (1 - p);
      var half = 50 * (1 - p);
      var value;
      if (direction === "left") value = "inset(0 " + amount + "% 0 0)";
      else if (direction === "right") value = "inset(0 0 0 " + amount + "%)";
      else if (direction === "up") value = "inset(" + amount + "% 0 0 0)";
      else if (direction === "down") value = "inset(0 0 " + amount + "% 0)";
      else value = "inset(" + half + "% " + half + "% " + half + "% " + half + "%)";
      el.style.clipPath = value;
    }

    onEnter(
      el,
      threshold,
      function () {
        var run = function () {
          tween(duration, ease, frame, function () {
            el.style.clipPath = "";
            el.style.willChange = "";
            setState(el, "revealed");
          });
        };
        if (delay > 0) window.setTimeout(run, delay);
        else run();
      },
      once
    );
  }

  /* ── pin-section ───────────────────────────────────────────────
   *
   * Holds its own root in place for a bounded stretch of scroll. The
   * sticking is CSS position:sticky; the runtime only adds the spacer
   * that gives the pin something to last for, and publishes state.
   * ───────────────────────────────────────────────────────────────── */

  function initPinSection(el) {
    var distance = num(el, "distance", 600, 0, 4000);
    var edge = pick(el, "edge", ["top", "bottom"], "top");

    if (reducedMotion()) {
      setState(el, "static");
      return;
    }
    if (!el.style || !el.parentElement) return;

    el.style.position = "sticky";
    el.style[edge] = "0px";

    // The spacer is what the page scrolls through while the section
    // stays put. Created here rather than asked for in markup, because a
    // spacer in the markup is a mysterious empty div in the Designer.
    var spacer = document.createElement("div");
    spacer.setAttribute(PART, "pin-spacer");
    spacer.setAttribute("aria-hidden", "true");
    spacer.style.height = distance + "px";
    spacer.style.pointerEvents = "none";
    if (el.nextSibling) el.parentElement.insertBefore(spacer, el.nextSibling);
    else el.parentElement.appendChild(spacer);

    var pinned = null;
    onScroll(function () {
      if (!el.getBoundingClientRect) return;
      var box = el.getBoundingClientRect();
      var now = edge === "top" ? box.top <= 0.5 : box.bottom >= viewportHeight() - 0.5;
      if (now === pinned) return;
      pinned = now;
      setState(el, now ? "pinned" : null);
    }, el);
  }

  /* ── horizontal-scroll ─────────────────────────────────────────
   *
   * Turns vertical scroll into horizontal movement of one owned track.
   * The distance is measured from the track's own overflow, so a
   * component does not have to guess a pixel number that only happens to
   * be right at one viewport width.
   * ───────────────────────────────────────────────────────────────── */

  function initHorizontalScroll(root) {
    var track = part(root, "track");
    if (!track) return;

    var direction = pick(root, "direction", ["left", "right"], "left");
    var stated = num(root, "distance", 0, 0, 20000);
    var startAt = num(root, "start", 0, 0, 1);
    var endAt = num(root, "end", 1, 0, 1);
    if (endAt <= startAt) {
      startAt = 0;
      endAt = 1;
    }

    if (reducedMotion()) {
      // Readable and scrollable by hand rather than moving by itself.
      if (root.style) root.style.overflowX = "auto";
      setState(root, "static");
      return;
    }

    if (root.style) root.style.overflow = "hidden";
    if (track.style) {
      track.style.display = "flex";
      track.style.flexWrap = "nowrap";
      track.style.willChange = "transform";
    }

    function span() {
      if (stated > 0) return stated;
      // Measured, not guessed: how much wider the track is than its
      // container, which is exactly how far it has to travel.
      var inner = track.scrollWidth || 0;
      var outer = root.getBoundingClientRect ? root.getBoundingClientRect().width : 0;
      var over = inner - outer;
      return over > 0 ? Math.min(over, 20000) : 0;
    }

    onScroll(function () {
      if (!root.getBoundingClientRect) return;
      var box = root.getBoundingClientRect();
      var vh = viewportHeight();
      var travel = box.height + vh;
      if (travel <= 0) return;
      var raw = (vh - box.top) / travel;
      var p = clamp01((clamp01(raw) - startAt) / (endAt - startAt));
      var move = span() * p * (direction === "left" ? -1 : 1);
      if (track.style) track.style.transform = "translate3d(" + move + "px,0,0)";
    }, root);

    setState(root, "ready");
  }

  /* ── image-stack ───────────────────────────────────────────────
   *
   * Owned items stack as the section scrolls: each one settles slightly
   * smaller and slightly offset behind the one after it. The order is
   * DOM order and cannot be configured.
   * ───────────────────────────────────────────────────────────────── */

  function initImageStack(root) {
    var items = ownedItems(root);
    if (items.length < 2) return;

    var offset = num(root, "offset", 24, 0, 200);
    var scaleStep = num(root, "scale", 0.04, 0, 0.4);
    var startAt = num(root, "start", 0, 0, 1);
    var endAt = num(root, "end", 1, 0, 1);
    if (endAt <= startAt) {
      startAt = 0;
      endAt = 1;
    }

    if (reducedMotion()) {
      setState(root, "static");
      return;
    }

    for (var i = 0; i < items.length; i++) {
      if (!items[i].style) continue;
      items[i].style.position = "sticky";
      items[i].style.top = i * offset + "px";
      items[i].style.willChange = "transform";
    }

    onScroll(function () {
      if (!root.getBoundingClientRect) return;
      var box = root.getBoundingClientRect();
      var vh = viewportHeight();
      var travel = box.height + vh;
      if (travel <= 0) return;
      var raw = (vh - box.top) / travel;
      var p = clamp01((clamp01(raw) - startAt) / (endAt - startAt));
      for (var i = 0; i < items.length; i++) {
        // Every card except the last shrinks as the stack builds on it.
        var behind = items.length - 1 - i;
        var shrink = 1 - scaleStep * behind * p;
        if (items[i].style) items[i].style.transform = "scale(" + shrink + ")";
      }
    }, root);

    setState(root, "ready");
  }

  /* ── text-highlight ────────────────────────────────────────────
   *
   * Activates owned word units in DOM order as the section scrolls past.
   * The runtime sets state and nothing else; CSS decides what active
   * looks like.
   * ───────────────────────────────────────────────────────────────── */

  function initTextHighlight(root) {
    var words = parts(root, "word");
    if (!words.length) return;

    var startAt = num(root, "start", 0, 0, 1);
    var endAt = num(root, "end", 1, 0, 1);
    if (endAt <= startAt) {
      startAt = 0;
      endAt = 1;
    }

    if (reducedMotion()) {
      for (var r = 0; r < words.length; r++) setState(words[r], "active");
      return;
    }

    for (var i = 0; i < words.length; i++) setState(words[i], "idle");
    var lit = -1;

    onScroll(function () {
      if (!root.getBoundingClientRect) return;
      var box = root.getBoundingClientRect();
      var vh = viewportHeight();
      var travel = box.height + vh;
      if (travel <= 0) return;
      var raw = (vh - box.top) / travel;
      var p = clamp01((clamp01(raw) - startAt) / (endAt - startAt));
      var upTo = Math.floor(p * words.length) - 1;
      if (upTo === lit) return;
      lit = upTo;
      for (var i = 0; i < words.length; i++) {
        setState(words[i], i <= upTo ? "active" : "idle");
      }
    }, root);

    setState(root, "ready");
  }

  /* ── parallax-layers ───────────────────────────────────────────
   *
   * Three owned depths at three multipliers the runtime chooses. A
   * component says which layer is which; it does not say how fast.
   * ───────────────────────────────────────────────────────────────── */

  var LAYER_SPEEDS = { "layer-slow": 0.08, "layer-medium": 0.16, "layer-fast": 0.28 };

  function initParallaxLayers(root) {
    var intensity = num(root, "intensity", 1, 0, 2);
    var axis = pick(root, "axis", ["y", "x"], "y");
    var max = num(root, "max", 120, 0, 800);

    var layers = [];
    for (var name in LAYER_SPEEDS) {
      if (!Object.prototype.hasOwnProperty.call(LAYER_SPEEDS, name)) continue;
      var found = parts(root, name);
      for (var i = 0; i < found.length; i++) {
        layers.push({ el: found[i], speed: LAYER_SPEEDS[name] });
        if (found[i].style) found[i].style.willChange = "transform";
      }
    }
    if (!layers.length) return;

    if (reducedMotion() || intensity === 0) {
      setState(root, "static");
      return;
    }

    onScroll(function () {
      if (!root.getBoundingClientRect) return;
      var box = root.getBoundingClientRect();
      var vh = viewportHeight();
      var centre = box.top + box.height / 2;
      var delta = centre - vh / 2;
      for (var i = 0; i < layers.length; i++) {
        var move = delta * layers[i].speed * intensity;
        if (move > max) move = max;
        if (move < -max) move = -max;
        if (!layers[i].el.style) continue;
        layers[i].el.style.transform =
          axis === "y"
            ? "translate3d(0," + move + "px,0)"
            : "translate3d(" + move + "px,0,0)";
      }
    }, root);

    setState(root, "ready");
  }

  /* ═════════════════════════════════════════════════════════════════
   * REGISTRY AND SCANNER
   *
   * The one place that knows the whole list. A behaviour is a name and
   * a function; adding one is a line here and a module above, and
   * removing one leaves nothing behind because nothing else references
   * it by name.
   * ═════════════════════════════════════════════════════════════════ */

  var BEHAVIORS = {
    reveal: initReveal,
    countup: initCountUp,
    marquee: initMarquee,
    carousel: initCarousel,
    accordion: initAccordion,
    tabs: initTabs,
    countdown: initCountdown,
    scrollspy: initScrollSpy,
    compare: initCompare,
    modal: initModal,
    "modal-open": initModalOpen,
    sticky: initSticky,
    parallax: initParallax,
    scrollprogress: initScrollProgress,
    pointer: initPointer,
    media: initMedia,
    toggle: initToggle,
    inview: initInView,
    scrollstate: initScrollState,
    tooltip: initTooltip,
    "hero-sequence": initHeroSequence,
    "split-reveal": initSplitReveal,
    "mask-reveal": initMaskReveal,
    "pin-section": initPinSection,
    "horizontal-scroll": initHorizontalScroll,
    "image-stack": initImageStack,
    "text-highlight": initTextHighlight,
    "parallax-layers": initParallaxLayers,
  };

  /**
   * Which elements have been initialised.
   *
   * A WeakSet rather than the data-backpack-ready attribute, because
   * markup can set an attribute and markup is exactly what this runtime
   * meets. A component carrying data-backpack-ready="true" used to make
   * the scanner skip it, so a behaviour could be silently suppressed by
   * the payload the behaviour came from. The attribute is still written,
   * because CSS and a person inspecting the page both want to see it, but
   * nothing is decided by reading it back.
   *
   * Weak, so an element removed from the document is collectable and the
   * set cannot grow without bound on a long-lived page.
   */
  var initialised = typeof WeakSet === "function" ? new WeakSet() : null;

  function alreadyInitialised(el) {
    if (initialised) return initialised.has(el);
    // No WeakSet means a browser old enough that the attribute is the
    // only option left. It is weaker, and it is better than initialising
    // the same carousel twice.
    return el.getAttribute(READY) === "true";
  }

  function markInitialised(el) {
    if (initialised) initialised.add(el);
    if (typeof el.setAttribute === "function") el.setAttribute(READY, "true");
  }

  /**
   * Initialise one element, at most once, and never fatally.
   *
   * A behaviour that throws costs that element its behaviour and
   * nothing else. The alternative, an exception escaping into the
   * scanner, would take every later element on the page with it, and
   * the visible result would be a page where some sections animate and
   * the rest are mysteriously dead.
   */
  function initOne(el) {
    if (!el || el.nodeType !== 1) return;
    if (alreadyInitialised(el)) return;

    var name = String(el.getAttribute(ATTR) || "").trim().toLowerCase();
    if (!name) return;
    if (!Object.prototype.hasOwnProperty.call(BEHAVIORS, name)) return;

    // Rule 4, applied at the single point every behaviour goes through
    // rather than repeated in thirteen places.
    if (isOffLimits(el)) return;

    markInitialised(el);
    try {
      BEHAVIORS[name](el);
    } catch (err) {
      setState(el, "failed");
      if (window.console && typeof window.console.warn === "function") {
        window.console.warn("[backpack] " + name + " failed to start", err);
      }
    }
  }

  function scan(within) {
    var scope = within && typeof within.querySelectorAll === "function" ? within : document;
    var found;
    try {
      found = scope.querySelectorAll("[" + ATTR + "]");
    } catch (err) {
      return;
    }
    for (var i = 0; i < found.length; i++) initOne(found[i]);

    // A scope that is itself a behaviour root, which querySelectorAll
    // does not return.
    if (
      scope !== document &&
      typeof scope.hasAttribute === "function" &&
      scope.hasAttribute(ATTR)
    ) {
      initOne(scope);
    }
  }

  /**
   * Rescan on DOM changes.
   *
   * Webflow publishes static HTML, so the first scan does almost all of
   * the work. This exists for the cases where it does not: CMS
   * collection lists that render late, tabs that mount their panels on
   * first open, and anything a site's own scripts add afterwards. It
   * watches for added elements only, and initOne is idempotent, so a
   * noisy page costs a cheap walk rather than duplicated behaviour.
   */
  function watch() {
    if (typeof window.MutationObserver !== "function") return;

    // ADDED SUBTREES ONLY, NOT THE WHOLE DOCUMENT.
    //
    // The first version re-ran querySelectorAll("[data-backpack]") over
    // the entire document on any addition. On a page where something
    // appends in a loop, that is a full-document walk per batch, and the
    // cost grows with the page rather than with the change. Now each
    // added element is scanned in place: the work is proportional to what
    // actually arrived.
    //
    // childList and subtree only. Attributes are not observed, so the
    // runtime's own writes cannot retrigger it and no observer loop is
    // possible; characterData is not observed, so nothing a visitor types
    // is ever seen here.
    var queue = [];
    var pending = false;

    function drain() {
      pending = false;
      var batch = queue;
      queue = [];
      for (var i = 0; i < batch.length; i++) {
        // Still connected, because a node added and removed inside one
        // batch has nothing worth initialising.
        if (batch[i].isConnected !== false) scan(batch[i]);
      }
    }

    var observer = new window.MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        if (!added) continue;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) queue.push(added[j]);
        }
      }
      if (!queue.length || pending) return;
      pending = true;
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(drain);
      } else {
        window.setTimeout(drain, 0);
      }
    });

    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * A PAGE-LEVEL READY MARKER, AS A DATA ATTRIBUTE.
   *
   * Webflow's App Review Preflight browser test asks for a ready signal it
   * can resolve with `document.querySelector(...)`, and says in as many
   * words: "A JavaScript flag such as `window.vendor.ready` is not a CSS
   * selector. Have the runtime's ready event add a data attribute first."
   *
   * `window.__backpackRuntime` is a flag, not a selector, so it cannot be
   * used. Neither can `[data-backpack-ready]`, which this runtime sets on
   * each element it takes over: that appears as soon as the FIRST element
   * initialises, and never at all on a page whose behaviours were all
   * refused or where a component carries none. The test needs something
   * that appears exactly once, after everything is usable.
   *
   * So: one attribute on the document element, written at the end of
   * `start()`, after the initial scan has run and the observer is watching.
   * By that point every behaviour on the page has initialised and any
   * element added later is handled by the observer, so the runtime is
   * fully usable when this appears.
   *
   * Selector for the test field:  [data-backpack-runtime]
   *
   * It carries the version so a person reading View Source, or a reviewer
   * checking which build is live, gets the answer without opening devtools.
   *
   * Safe against the observer: `watch()` observes childList and subtree
   * only, never attributes, so this write cannot retrigger a scan.
   */
  function markReady() {
    try {
      var root = document.documentElement || document.body;
      if (root && typeof root.setAttribute === "function") {
        root.setAttribute("data-backpack-runtime", VERSION);
      }
    } catch (err) {
      // A ready marker is diagnostic. It must never be the reason a
      // customer's page loses its animations.
    }
  }

  function start() {
    scan(document);
    watch();
    markReady();
  }

  window.__backpackRuntime = {
    version: VERSION,
    behaviors: (function () {
      var names = [];
      for (var key in BEHAVIORS) {
        if (Object.prototype.hasOwnProperty.call(BEHAVIORS, key)) names.push(key);
      }
      return names.sort();
    })(),
    // Exposed so a component added to the canvas after load, or a page
    // that renders its content late, can be picked up without waiting
    // for the observer. It is the same idempotent scan, so calling it
    // twice does nothing the first call did not already do.
    scan: function (within) {
      scan(within);
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
